import React from "react";

export const CARD_WIDTH = "3.375in";
export const CARD_HEIGHT = "2.125in";

export function CardFace({ children }) {
  return (
    <div
      className="bg-white border border-dashed border-gray-400 rounded-xl overflow-hidden relative shadow-sm"
      style={{
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        breakInside: "avoid",
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }}
    >
      {children}
    </div>
  );
}

function FieldRow({ label, value, valueClassName = "" }) {
  return (
    <div className="grid grid-cols-[74px_1fr] items-baseline gap-x-1.5 leading-tight">
      <span className="text-[8px] font-bold uppercase tracking-[0.02em]" style={{ color: "#154E93" }}>
        {label}
      </span>
      <span className={`text-[8.5px] font-semibold text-gray-900 truncate ${valueClassName}`}>{value || "—"}</span>
    </div>
  );
}

export function IDCardFront({ data, photoUrl, qrCode, displayName }) {
  const role = data.position || data.caregiver_type || "Employee";
  const department = data.department || data.staff_category || "—";

  return (
    <CardFace>
      <div className="absolute inset-x-0 top-0 h-[52px]" style={{ background: "linear-gradient(100deg,#082b5c,#154E93,#2c73b8)" }} />
      <div className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-white/10" />

      <div className="relative h-full flex flex-col px-3 pt-2.5 pb-2" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
        <div className="flex items-center justify-between text-white h-[31px]">
          <div className="flex items-center gap-2 min-w-0">
            <img src="/logo.png" alt="Jybek HomeCare Services" className="h-7 w-auto bg-white rounded p-0.5 shrink-0" />
            <div className="leading-tight min-w-0">
              <div className="text-[8px] font-bold tracking-[0.04em] truncate">JYBEK HOMECARE</div>
              <div className="text-[6.5px] text-blue-100 tracking-[0.08em]">SERVICES</div>
            </div>
          </div>
          <div className="text-[6.5px] font-semibold uppercase tracking-[0.08em] text-blue-100 shrink-0">
            Employee ID
          </div>
        </div>

        <div className="flex flex-1 items-center gap-3 pt-2.5 min-h-0">
          <div className="relative shrink-0">
            <img src={photoUrl} alt={displayName} className="w-[76px] h-[92px] rounded-lg object-cover border-2 border-white shadow" />
            <span className="absolute -bottom-1 -right-1 w-[17px] h-[17px] rounded-full bg-emerald-500 border-2 border-white" />
          </div>

          <div className="flex-1 min-w-0 self-stretch flex flex-col justify-center">
            <div className="text-[14px] font-extrabold leading-[1.05] uppercase truncate" style={{ color: "#0b2f63" }}>
              {displayName}
            </div>
            <div className="text-[10px] font-bold text-blue-700 leading-tight truncate mt-1">{role}</div>
            {data.caregiver_type && data.position && data.caregiver_type !== data.position && (
              <div className="text-[8px] font-semibold text-gray-500 uppercase truncate mt-0.5">{data.caregiver_type}</div>
            )}

            <div className="mt-2 space-y-1">
              <FieldRow label="Employee ID" value={data.employee_id} />
              <FieldRow label="Department" value={department} />
              <FieldRow label="Issued" value={data.id_card_issue_date} />
              <FieldRow label="Expires" value={data.id_card_expiration_date} />
            </div>
          </div>

          {qrCode && (
            <div className="text-center shrink-0 self-center">
              <img src={qrCode} alt="Employee verification QR code" className="w-[58px] h-[58px] bg-white p-0.5 rounded border" />
              <div className="text-[6.5px] font-semibold text-gray-500 mt-1 whitespace-nowrap">SCAN TO VERIFY</div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 mt-1.5">
          <span className="w-3.5 h-3.5 rounded-full bg-emerald-500 shrink-0" />
          <span className="text-[9px] font-bold text-emerald-700">ACTIVE</span>
          <div className="h-1 flex-1 rounded-full ml-1" style={{ background: "linear-gradient(90deg,#154E93,#45a7d8,#42b883)" }} />
        </div>
      </div>
    </CardFace>
  );
}

export function IDCardBack({ data, displayName }) {
  const role = data.position || data.caregiver_type || "—";
  const department = data.department || data.staff_category || "—";

  return (
    <CardFace>
      <div className="absolute inset-x-0 top-0 h-[43px]" style={{ background: "linear-gradient(100deg,#082b5c,#154E93)" }} />

      <div className="relative h-full flex flex-col px-3 pt-2 pb-2 text-gray-800" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
        <div className="h-[27px] flex items-center justify-between text-white">
          <span className="text-[8.5px] font-bold tracking-[0.04em]">JYBEK HOMECARE SERVICES</span>
          <span className="text-[6.5px] font-semibold text-blue-100">AUTHORIZED EMPLOYEE</span>
        </div>

        <div className="pt-2.5 grid grid-cols-[1fr_112px] gap-3 flex-1 min-h-0">
          <div className="min-w-0">
            <p className="mb-2 text-[8px] leading-[1.35] text-gray-700">
              This card identifies the bearer as an authorized employee of Jybek HomeCare Services.
            </p>

            <div className="space-y-1.5">
              <FieldRow label="Employee" value={displayName} />
              <FieldRow label="Position" value={role} />
              <FieldRow label="Employee ID" value={data.employee_id} />
              <FieldRow label="Department" value={department} />
              <FieldRow label="Issued" value={data.id_card_issue_date} />
              <FieldRow label="Expires" value={data.id_card_expiration_date} />
            </div>
          </div>

          <div className="border-l pl-3 min-w-0">
            <p className="font-bold text-[9px] mb-2" style={{ color: "#154E93" }}>COMPANY CONTACT</p>
            <div className="space-y-1.5 text-[8px] leading-[1.25] text-gray-800">
              <p>15 Constitution Dr</p>
              <p>Bedford, NH 03110</p>
              <p className="pt-0.5">1-888-717-5009</p>
              <p className="break-all">info@jybekhomecares.com</p>
              <p className="break-all">jybekhomecares.com</p>
            </div>
          </div>
        </div>

        <div className="border-t pt-1.5 text-[6.5px] text-gray-500 flex justify-between gap-2 leading-tight">
          <span>If found, please return this card to the company.</span>
          <span className="font-semibold text-right">Property of Jybek HomeCare Services</span>
        </div>
      </div>
    </CardFace>
  );
}
